/* rk-lhc.js — JS de la familia rk-lhc-* (PDPs de kits de cuero migradas de GemPages; primera:
   /products/leather-heart-charm). Complementa a rk-alk.js (rk-atc, rk-gallery, rk-accordion).
   Calco del comportamiento medido en el original (16-sep-2026):
   - elegir un color cambia la variante de TODOS los formularios de compra sincronizados de la
     página (buybox, CTA suelto, sticky), salta la galería a la imagen de la variante y escribe
     ?variant= en la URL;
   - el stepper de cantidad (mín. 1, +/− y tipeo) sincroniza la cantidad de esos mismos forms.
   Los forms sincronizados son los que viven dentro de un ancestro con [data-rk-lhc-sync]
   (los related NO se sincronizan, como en el original). Guardas customElements.get(): el script
   se incluye desde varias secciones. */

(function () {
  const SYNC = '[data-rk-lhc-sync]';

  function syncedInputs(name) {
    return [...document.querySelectorAll(`${SYNC} form input[name="${name}"]`)];
  }

  function setVariantEverywhere(id) {
    for (const input of syncedInputs('id')) input.value = id;
  }

  function setQuantityEverywhere(qty) {
    for (const input of syncedInputs('quantity')) input.value = qty;
  }

  /* ---------- selector de color (swatches) ---------- */
  if (!customElements.get('rk-lhc-variants')) {
    class RkLhcVariants extends HTMLElement {
      connectedCallback() {
        this.buttons = [...this.querySelectorAll('[data-rk-variant]')];
        for (const btn of this.buttons) {
          btn.addEventListener('click', () => this.#select(btn));
        }
      }

      #select(btn) {
        const id = btn.dataset.rkVariant;
        for (const b of this.buttons) {
          const on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        setVariantEverywhere(id);
        // precio de la variante (todas las variantes de este producto cuestan lo mismo hoy;
        // se actualiza igual por si cambian los precios en el admin)
        const price = btn.dataset.rkPrice;
        if (price) {
          for (const el of document.querySelectorAll(`${SYNC} [data-rk-lhc-price]`)) {
            if (el.textContent.trim() !== price) el.textContent = price;
          }
        }
        // URL ?variant= (como GemPages)
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', id);
          window.history.replaceState({}, '', url);
        } catch (error) { /* sin history en webviews raros: no pasa nada */ }
        // galería: saltar a la imagen de la variante (el original mueve el slider al featured media)
        const mediaId = btn.dataset.rkMedia;
        if (mediaId) {
          /* Dos galerias posibles y con nombres de atributo distintos:
             - la propia de la seccion (markup rk-lhc):      [data-rk-thumb][data-rk-media]
             - la COMPARTIDA del theme (rk-gallery.liquid):  [data-rk-gal-thumb][data-media-id]
             Antes solo se buscaba la propia, y ademas scopeada a un <rk-gallery> que no existe
             en la pagina: por eso el color no movia la imagen (verificado 15-oct-2026). */
          const propia = document.querySelector(`[data-rk-thumb][data-rk-media="${mediaId}"]`);
          const compartida = document.querySelector(`[data-rk-gal-thumb][data-media-id="${mediaId}"]`);
          const thumb = propia || compartida;
          if (thumb) thumb.click();
        }
        this.dispatchEvent(new CustomEvent('rk-lhc:variant', { bubbles: true, detail: { id } }));
      }
    }
    customElements.define('rk-lhc-variants', RkLhcVariants);
  }

  /* ---------- stepper de cantidad ---------- */
  if (!customElements.get('rk-lhc-qty')) {
    class RkLhcQty extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input');
        this.min = parseInt(this.input?.min || '1', 10) || 1;
        this.querySelector('[data-rk-minus]')?.addEventListener('click', () => this.#set(this.#value() - 1));
        this.querySelector('[data-rk-plus]')?.addEventListener('click', () => this.#set(this.#value() + 1));
        this.input?.addEventListener('change', () => this.#set(this.#value()));
        this.input?.addEventListener('input', () => {
          // solo dígitos mientras se tipea (inputmode numeric, como el original)
          const clean = this.input.value.replace(/[^0-9]/g, '');
          if (clean !== this.input.value) this.input.value = clean;
          if (clean) setQuantityEverywhere(Math.max(this.min, parseInt(clean, 10)));
        });
        this.input?.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') { event.preventDefault(); this.#set(this.#value()); this.input.blur(); }
        });
      }

      #value() {
        const n = parseInt(this.input?.value || '', 10);
        return Number.isFinite(n) ? n : this.min;
      }

      #set(n) {
        const qty = Math.max(this.min, n);
        if (this.input) this.input.value = qty;
        setQuantityEverywhere(qty);
        this.dispatchEvent(new CustomEvent('rk-lhc:qty', { bubbles: true, detail: { qty } }));
      }
    }
    customElements.define('rk-lhc-qty', RkLhcQty);
  }
  /* ---------- acordeón FAQ (details nativo + animación 500ms como el gp-accordion) ---------- */
  if (!customElements.get('rk-lhc-accordion')) {
    class RkLhcAccordion extends HTMLElement {
      connectedCallback() {
        this.duration = parseInt(this.dataset.rkDuration || '500', 10) || 500;
        for (const details of this.querySelectorAll('details')) {
          const summary = details.querySelector('summary');
          const content = details.querySelector('[data-rk-panel]');
          if (!summary || !content) continue;
          summary.addEventListener('click', (event) => {
            event.preventDefault();
            this.#toggle(details, content);
          });
        }
      }

      #toggle(details, content) {
        if (details.dataset.animating) return;
        // acordeón de UN solo ítem abierto (medido en el original: abrir uno cierra el anterior)
        if (!details.open) {
          for (const other of this.querySelectorAll('details[open]')) {
            if (other !== details) this.#toggle(other, other.querySelector('[data-rk-panel]'));
          }
        }
        details.dataset.animating = '1';
        const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
        if (details.open) {
          const anim = content.animate(
            [{ blockSize: `${content.scrollHeight}px` }, { blockSize: '0px' }],
            { duration: this.duration, easing }
          );
          anim.onfinish = () => {
            details.open = false;
            delete details.dataset.animating;
          };
        } else {
          details.open = true;
          const anim = content.animate(
            [{ blockSize: '0px' }, { blockSize: `${content.scrollHeight}px` }],
            { duration: this.duration, easing }
          );
          anim.onfinish = () => {
            delete details.dataset.animating;
          };
        }
      }
    }
    customElements.define('rk-lhc-accordion', RkLhcAccordion);
  }
  /* ---------- tira de miniaturas circular (calco del carrusel de galería GP, loop: true) ---------- */
  if (!customElements.get('rk-lhc-thumbs')) {
    class RkLhcThumbs extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rk-thumbs]');
        this.originals = [...this.querySelectorAll('[data-rk-thumb]')];
        this.clones = [...this.querySelectorAll('[data-rk-clone]')];
        this.n = this.originals.length;
        if (!this.track || this.n < 2) return;
        // un clon se comporta como su miniatura original (rk-gallery mueve la imagen principal)
        for (const clone of this.clones) {
          clone.addEventListener('click', () => this.originals[+clone.dataset.rkClone]?.click());
        }
        // espejo del estado activo (rk-gallery solo marca los originales)
        const observer = new MutationObserver(() => this.#mirrorActive());
        for (const original of this.originals) observer.observe(original, { attributes: true, attributeFilter: ['class'] });
        this.#mirrorActive();
        // arrancar en la copia del medio (la 1ª miniatura a la izquierda, como el original)
        this.#recenter();
        requestAnimationFrame(() => this.#recenter());
        window.addEventListener('load', () => this.#recenter(), { once: true });
        window.addEventListener('resize', () => this.#recenter());
        // tras cada desplazamiento (flecha, drag o auto-seguimiento), volver a la copia del medio sin que se note
        let timer;
        this.track.addEventListener('scroll', () => { clearTimeout(timer); timer = setTimeout(() => this.#normalize(), 140); }, { passive: true });
        this.track.addEventListener('scrollend', () => this.#normalize());
      }

      #span() {
        const a = this.originals[0], b = this.originals[1];
        return (b.getBoundingClientRect().left - a.getBoundingClientRect().left) * this.n;
      }

      #recenter() {
        const span = this.#span();
        if (span > 0) this.track.scrollLeft = span;
      }

      #normalize() {
        const span = this.#span();
        if (!(span > 0)) return;
        const x = this.track.scrollLeft;
        if (x < span * 0.5) this.track.scrollLeft = x + span;
        else if (x > span * 1.5) this.track.scrollLeft = x - span;
      }

      #mirrorActive() {
        const active = this.originals.map((o) => o.classList.contains('is-active'));
        for (const clone of this.clones) clone.classList.toggle('is-active', !!active[+clone.dataset.rkClone]);
      }
    }
    customElements.define('rk-lhc-thumbs', RkLhcThumbs);
  }
})();
